import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/ui/phone-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COUNTRY_CODE_ISO_2, PHONE_LENGTH_BY_COUNTRY } from "@/constants/countries.constants";
import { useAppDispatch, useAppSelector } from "@/hooks/redux-hooks";
import { applyFiltersAndSearch } from "@/store/reducers/leads.reducer";
import { getLeadStatusList } from "@/store/reducers/leads.status.reducer";
import { TCreateLeadPayload } from "@/models/leads.model";
import { getLeadDetailsUrl } from "@/routes/paths";
import leadsService from "@/services/leads.service";
import { getErrorFromBackend } from "@/utils/errors";
import { isValidEmail } from "@/utils/strings";
import { Loader2, UserPlus } from "lucide-react";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Quick-create "New Lead" (demo-ui AddLeadModal): a minimal manual lead form
 * over the existing POST /lead (leadsService.createLead). On success it
 * navigates to the new lead's details page.
 *
 * Status comes from the company's own catalog (GET /lead/status, ordered by
 * `order ASC`), so the first entry is also the status the backend assigns when
 * none is sent. The pick travels in the POST body as `statusId`.
 */

type Props = {
  open: boolean;
  onClose: () => void;
};

const LABEL_CLASS = "mb-1.5 block text-[10.5px] font-semibold uppercase tracking-wider text-slate-500";
const INPUT_CLASS =
  "h-10 rounded-xl border-white/[0.06] bg-white/[0.02] px-3 text-[12.5px] text-slate-200 placeholder:text-slate-500 focus:border-brand-500/40 focus-visible:ring-0 focus-visible:ring-offset-0";

// inline-block + margin rather than a flex row: SelectTrigger forces
// `line-clamp-1` on its direct child span, which would drop a flex wrapper
// back to an inline layout and collapse the dot to zero width.
const StatusDot = ({ color }: { color: string }) => (
  <span
    className="mr-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full align-middle"
    style={{ backgroundColor: color }}
  />
);

const AddLeadModal = ({ open, onClose }: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { list: statusList } = useAppSelector((s) => s.leadStatusList);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [country, setCountry] = useState<COUNTRY_CODE_ISO_2>("US" as COUNTRY_CODE_ISO_2);
  const [statusId, setStatusId] = useState<number>();
  const [saving, setSaving] = useState(false);

  const defaultStatus = statusList[0];

  useEffect(() => {
    if (open) {
      setFirstName("");
      setLastName("");
      setEmail("");
      setPhone("");
      setStatusId(undefined);
      setSaving(false);
      // Cached after the first call — the leads page already primes it.
      dispatch(getLeadStatusList());
    }
  }, [open, dispatch]);

  // Preselect the first status of the catalog, once it's available (the list
  // may still be loading when the modal opens).
  useEffect(() => {
    if (open && statusId === undefined && defaultStatus) setStatusId(defaultStatus.id);
  }, [open, statusId, defaultStatus]);

  const canSave = firstName.trim().length > 0 && isValidEmail(email.trim());
  const maxLength = PHONE_LENGTH_BY_COUNTRY[country]?.max ?? 15;
  const selectedStatus = statusList.find((s) => s.id === statusId);

  const handleCreate = async () => {
    if (!canSave || saving) return;
    const payload: TCreateLeadPayload = {
      firstName: firstName.trim(),
      email: email.trim(),
      ...(lastName.trim() ? { lastName: lastName.trim() } : {}),
      ...(phone.trim() ? { phone: phone.trim(), phoneCountryCode: country } : {}),
      ...(statusId ? { statusId } : {}),
    };
    try {
      setSaving(true);
      const lead = await leadsService.createLead(payload);
      // The leads list caches its pages in redux; refresh it (keeping the
      // user's filters) so the new lead shows up without a manual reload.
      // Fire-and-forget — navigation shouldn't wait on the list.
      dispatch(applyFiltersAndSearch({ page: 1 }));
      enqueueSnackbar("Lead created successfully", { variant: "success" });
      onClose();
      navigate(getLeadDetailsUrl(lead.id));
    } catch (e) {
      enqueueSnackbar(getErrorFromBackend(e), { variant: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-2xl border border-white/[0.08] bg-gradient-to-br from-ink-800/95 to-ink-900/95 shadow-[0_24px_80px_-12px_rgba(0,0,0,0.7)] backdrop-blur-xl sm:max-w-md">
        <div className="pointer-events-none absolute -right-20 -top-20 h-48 w-48 rounded-full bg-brand-500/20 blur-3xl" />
        <DialogHeader className="relative">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-brand-400/30 to-brand-700/10 ring-1 ring-brand-400/30">
              <UserPlus className="h-4 w-4 text-brand-200" />
            </div>
            <div className="text-left">
              <DialogTitle className="text-[16px] font-semibold tracking-tight text-white">New Lead</DialogTitle>
              <p className="mt-0.5 text-[11.5px] text-slate-400">Capture a prospect manually</p>
            </div>
          </div>
        </DialogHeader>

        <div className="relative space-y-4 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label htmlFor="ql-first-name" className={LABEL_CLASS}>
                First name *
              </label>
              <Input
                id="ql-first-name"
                autoFocus
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Jane"
                className={INPUT_CLASS}
              />
            </div>
            <div>
              <label htmlFor="ql-last-name" className={LABEL_CLASS}>
                Last name
              </label>
              <Input
                id="ql-last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Doe"
                className={INPUT_CLASS}
              />
            </div>
          </div>

          <div>
            <label htmlFor="ql-email" className={LABEL_CLASS}>
              Email *
            </label>
            <Input
              id="ql-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@example.com"
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <span className={LABEL_CLASS}>Phone</span>
            <PhoneInput
              countryValue={country}
              phoneValue={phone}
              maxLength={maxLength}
              onCountryChange={(iso) => setCountry(iso)}
              onPhoneChange={setPhone}
            />
          </div>

          <div>
            <span className={LABEL_CLASS}>Status</span>
            <Select
              value={statusId ? String(statusId) : ""}
              disabled={!statusList.length}
              onValueChange={(v) => setStatusId(Number(v))}
            >
              <SelectTrigger className={INPUT_CLASS}>
                {selectedStatus ? (
                  <span>
                    <StatusDot color={selectedStatus.color} />
                    {selectedStatus.name}
                  </span>
                ) : (
                  <SelectValue placeholder="Loading statuses..." />
                )}
              </SelectTrigger>
              <SelectContent>
                {statusList.map((status) => (
                  <SelectItem key={status.id} value={String(status.id)}>
                    <StatusDot color={status.color} />
                    {status.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 items-center rounded-xl border border-white/[0.06] bg-white/[0.02] px-3 text-[12.5px] font-medium text-slate-300 transition hover:border-white/[0.14] hover:text-white"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleCreate}
              disabled={!canSave || saving}
              className="inline-flex h-9 items-center gap-1.5 rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 px-4 text-[12.5px] font-semibold text-white shadow-[0_8px_24px_-10px_rgba(59,130,246,0.7)] ring-1 ring-brand-400/40 transition hover:translate-y-[-1px] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0"
            >
              {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              {saving ? "Creating..." : "Create lead"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddLeadModal;
